import styled from "styled-components";
import Select from "react-select";
import { useRef, useState } from "react";

import {
	ComplexFilterType,
	ReservationsStatusComplexFilter,
} from "interfaces/filters";

import { reservationStatusesList } from "utils/reservations";
import { selectComponentStyles } from "utils/selectStyles";

import { useOnClickOutside } from "hooks/useOnClickOutside";

import { MAIN_THEME } from "styles/colors";
import { FONT_FAMILY_BOLD } from "styles/typography";
import { ReservationStatusSelectOption } from "interfaces/reservations";

interface ActionButtonProps {
	left?: boolean;
	right?: boolean;
	apply?: boolean;
	cancel?: boolean;
}

const Container = styled.div`
	position: absolute;
	left: 0;
	top: 45px;
	z-index: 1000;
	background: ${MAIN_THEME.DARK_BG_SECONDARY};
	border: 1px solid ${MAIN_THEME.BORDER_COLOR};
	box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
	backdrop-filter: blur(20px);
	border-radius: 15px;
	padding: 30px 40px;
	width: 360px;

	@media (max-width: 480px) {
		width: calc(100vw - 40px);
		padding: 20px 30px;
	}
`;

const Actions = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 10px;
	margin-top: 25px;
`;

const ActionButton = styled.button<ActionButtonProps>`
	display: flex;
	height: 54px;
	justify-content: center;
	align-items: center;
	border-top-left-radius: ${(props) => (props.left ? '109px' : 0)};
	border-bottom-left-radius: ${(props) => (props.left ? '109px' : 0)};
	border-top-right-radius: ${(props) => (props.right ? '109px' : 0)};
	border-bottom-right-radius: ${(props) => (props.right ? '109px' : 0)};
	border: none;
	background: ${(props) =>
		props.apply
			? `${MAIN_THEME.SECONDARY}`
			: MAIN_THEME.DARK_BG_TERTIARY};
	color: ${(props) => (props.apply ? MAIN_THEME.DARK_BG : MAIN_THEME.TEXT_SECONDARY)};
	font-family: ${FONT_FAMILY_BOLD};
	font-size: 16px;
	cursor: pointer;
	font-weight: 600;
	transition: background 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;

	&:hover:not(:disabled) {
		${(props) =>
			!props.apply
				? `box-shadow: 0 0 15px rgba(255, 97, 97, 0.15);`
				: 'transform: translateY(-1px);'}
	}

	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	@media (max-width: 480px) {
		height: 48px;
		font-size: 14px;
	}
`;

interface Props {
	onClose: () => void;
	onChange: (filter: ReservationsStatusComplexFilter) => void;
}

const ReservationsStatusFilter = (props: Props): JSX.Element => {
	const [selectedReservationStatus, setSelectedReservationStatus] =
		useState<ReservationStatusSelectOption | null>(null);

	const { onClose, onChange } = props;

	const ref = useRef(null);

	useOnClickOutside(ref, () => {
		onClose();
	});

	const handleChangeStatus = (value: ReservationStatusSelectOption | null) => {
		if (value) {
			setSelectedReservationStatus(value);
		}
	};

	const handleReset = () => {
		setSelectedReservationStatus(null);
		onChange({
			type: ComplexFilterType.STATUS,
			value: null,
		});

		onClose();
	};

	const handleApply = () => {
		onChange({
			type: ComplexFilterType.STATUS,
			value: selectedReservationStatus?.value || null,
		});

		onClose();
	};

	const renderApplyButton = () => (
		<ActionButton
			right
			apply
			type="submit"
			onClick={handleApply}
			disabled={!selectedReservationStatus}
		>
			Apply
		</ActionButton>
	);

	const renderResetButton = () => (
		<ActionButton left cancel onClick={handleReset}>
			Reset
		</ActionButton>
	);

	return (
		<Container ref={ref}>
			<Select
				value={selectedReservationStatus}
				onChange={handleChangeStatus}
				styles={{
					...selectComponentStyles,
				}}
				options={reservationStatusesList}
			/>
			<Actions>
				{renderResetButton()}
				{renderApplyButton()}
			</Actions>
		</Container>
	);
};

export default ReservationsStatusFilter;
